import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import { FormikHelpers, useFormikContext } from 'formik'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import { ArrowDown } from 'src/assets/icons/customIcons/ArrowDown'
import { useAdminAccess, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { Button } from 'src/tailwind/components/Button'
import { Section } from 'src/tailwind/components/Section'
import BackButton from 'src/views/components/BackButton'
import Checkbox from 'src/views/components/forms/formik/Checkbox'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import Loader from 'src/views/components/Loader'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'

type DegreeItem = Api.AdministratorFacultyDegree['attributes'][number]
type Degrees = Api.AdministratorFacultyDegree['attributes']
type FacultyBody = Api.putAdministratorFacultyParams['body']

interface FormValuesBase {
  code: string
  name: string
  nameEn: string
  status: string
  hasMajor: boolean
  hasMinor: boolean
  allowInRegistration: boolean
  allowInMinorRegistration: boolean
  mail: string
  feedbackMail: string
  consultationMail: string
  dean: string
}

interface FormValues extends FormValuesBase {
  [key: string]: string | number[] | boolean | undefined
}

const SEMESTERS = Array.from({ length: 30 }, (_, i) => i + 1)

function getInitialValuesFromFaculty(faculty: Api.AdministratorFaculty | undefined, degrees: Degrees): FormValues {
  const base: FormValuesBase = {
    code: faculty?.code ?? '',
    name: faculty?.name ?? '',
    nameEn: faculty?.nameEn ?? '',
    status: faculty?.status?.value?.toString() ?? '',
    allowInRegistration: faculty?.allowInRegistration ?? false,
    allowInMinorRegistration: faculty?.allowInMinorRegistration ?? false,
    mail: faculty?.mail ?? '',
    feedbackMail: faculty?.feedbackMail ?? '',
    consultationMail: faculty?.consultationMail ?? '',
    dean: faculty?.deanUser?.id ?? '',
    hasMajor: faculty?.hasMajorMinor?.value === 1 || faculty?.hasMajorMinor?.value === 3,
    hasMinor: faculty?.hasMajorMinor?.value === 2 || faculty?.hasMajorMinor?.value === 3,
  }
  const result: FormValues = { ...base }
  for (const degree of degrees) {
    const facultySemester = faculty?.[degree.key as keyof Api.AdministratorFaculty] as
      | Api.AdministratorFacultyDegreeSemester
      | undefined
    result[degree.key] = facultySemester?.semesters ? [...facultySemester.semesters] : []
  }
  return result
}

function formValuesToRequestBody(values: FormValues, degrees: Degrees): FacultyBody {
  const hasMajorMinor = values.hasMajor && values.hasMinor ? 3 : values.hasMajor ? 1 : values.hasMinor ? 2 : 0
  const body: Record<string, unknown> = {
    name: values.name,
    nameEn: values.nameEn,
    code: values.code,
    status: Number(values.status),
    dean: values.dean,
    mail: values.mail,
    feedbackMail: values.feedbackMail,
    consultationMail: values.consultationMail,
    allowInRegistration: values.allowInRegistration,
    allowInMinorRegistration: values.allowInMinorRegistration,
    hasMajorMinor,
  }
  for (const degree of degrees) {
    body[degree.key] = (values[degree.key] as number[] | undefined) ?? []
  }
  return body as FacultyBody
}

export default function AdministratorFacultyPage(): JSX.Element {
  const t = useTranslatable()
  return (
    <SuspenseWrapper title={t('faculty:faculty')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element | null {
  const { id } = useParams<{ id: string }>()
  const t = useTranslatable()
  const locale = useLocale()
  const navigate = useNavigate()
  const headers = useAuthenticatedHeaders()
  const [isSubmitted, setIsSubmitted] = useState(false)

  const facultiesPath = `/${locale}/administrator/administration/faculties`

  const { data: faculty, isLoading: isFacultyLoading } = useApi({
    endpoint: Api.getAdministratorFaculty,
    params: useMemo(() => ({ headers, args: { id: id! } }), [headers, id]),
    shouldFetch: id != null,
    suspense: false,
  })

  const { data: degreesData, isLoading: isDegreesLoading } = useApi({
    endpoint: Api.getAdministratorFacultyDegrees,
    params: useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })

  const degrees = useMemo(() => degreesData?.attributes ?? [], [degreesData?.attributes])
  const initialValues = useMemo(() => getInitialValuesFromFaculty(faculty ?? undefined, degrees), [faculty, degrees])

  const breadcrumbsItems = useMemo(
    () => [
      { page: t('faculty:faculty'), path: facultiesPath },
      { page: faculty?.name ?? t('faculty:add_faculty'), path: `${facultiesPath}/${id}` },
    ],
    [t, facultiesPath, faculty?.name, id]
  )

  const handleSubmit = useCallback(
    async (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
      const body = formValuesToRequestBody(values, degrees)
      try {
        if (id) {
          await Api.putAdministratorFaculty({ headers, args: { id }, body })
        } else {
          await Api.postAdministratorFaculty({ headers, body })
        }
        toast.success(t('common:updated_successfully'))
        setIsSubmitted(true)
        formikHelpers.resetForm()
      } catch {
        toast.error(t('error:an_error_occurred'))
      }
    },
    [headers, id, t, degrees]
  )

  useEffect(() => {
    if (isSubmitted) navigate(facultiesPath)
  }, [isSubmitted, navigate, facultiesPath])

  if (isFacultyLoading || isDegreesLoading) return <Loader className="m-auto flex" />

  return (
    <Section
      title={faculty?.name ?? t('faculty:add_faculty')}
      breadcrubms={breadcrumbsItems}
      icon={<FontAwesomeIcon icon={ProRegularSvgIcons.faUniversity} />}
      rightElement={<BackButton link={facultiesPath} />}
    >
      <Form isConfirmable={!isSubmitted} initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
        <FacultyFormContent degrees={degrees} />
      </Form>
    </Section>
  )
}

interface FacultyFormContentProps {
  readonly degrees: Degrees
}

function FacultyFormContent({ degrees }: FacultyFormContentProps): JSX.Element {
  const t = useTranslatable()
  const headers = useAuthenticatedHeaders()
  const { data: deanUsers, isLoading: isDeanUsersLoading } = useApi({
    endpoint: Api.getAdministratorDeanUsers,
    params: useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })
  const deanOptions = useMemo(
    () => (deanUsers ?? []).map((u) => ({ label: u.fullName ?? '', value: u.id ?? '' })),
    [deanUsers]
  )
  const statusOptions = useMemo(
    () => [
      { label: t('common:active'), value: '1' },
      { label: t('common:inactive'), value: '2' },
    ],
    [t]
  )

  return (
    <div className="w-full rounded-card bg-card px-6 py-9">
      <FormError />
      <BasicInfoSection statusOptions={statusOptions} />
      <ContactSection deanOptions={deanOptions} isDeanLoading={isDeanUsersLoading} />
      <HasProgramsSection />
      <SemesterConfigurationSection degrees={degrees} />
      <FormActionsSection />
    </div>
  )
}

function BasicInfoSection({
  statusOptions,
}: {
  readonly statusOptions: readonly { label: string; value: string }[]
}): JSX.Element {
  const t = useTranslatable()
  return (
    <section className="mb-6">
      <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <TextInput type="text" name="code" label={t('common:code')} required />
        <TextInput type="text" name="name" label={t('common:title')} required />
        <TextInput type="text" name="nameEn" label={`${t('common:name')} (EN)`} required />
        <SelectInput name="status" label={t('common:status')} options={[...statusOptions]} required />
      </div>
      <div className="mb-4 flex flex-col gap-4">
        <Checkbox name="allowInRegistration" label={t('faculty:allow_in_registration')} />
        <Checkbox name="allowInMinorRegistration" label={t('faculty:allow_in_minor_registration')} />
      </div>
    </section>
  )
}

function ContactSection({
  deanOptions,
  isDeanLoading,
}: {
  readonly deanOptions: readonly { label: string; value: string }[]
  readonly isDeanLoading: boolean
}): JSX.Element {
  const t = useTranslatable()
  return (
    <section className="mb-6">
      <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <TextInput type="email" name="mail" label={t('person:e_mail')} required />
        <TextInput type="email" name="feedbackMail" label={t('faculty:monitoring_email')} />
        <TextInput type="email" name="consultationMail" label={t('faculty:consultations_email')} />
        <SelectInput
          name="dean"
          label={t('faculty:dean')}
          options={deanOptions}
          isLoading={isDeanLoading}
          disabled={isDeanLoading}
        />
      </div>
    </section>
  )
}

function HasProgramsSection(): JSX.Element {
  const t = useTranslatable()
  return (
    <section className="mb-6">
      <label className="mb-2 block font-bold text-primaryTextColor">{t('faculty:has_programs')}</label>
      <div className="flex gap-4">
        <Checkbox name="hasMajor" label={t('program:major')} />
        <Checkbox name="hasMinor" label={t('program:minor')} />
      </div>
    </section>
  )
}

function SemesterConfigurationSection({ degrees }: { readonly degrees: Degrees }): JSX.Element {
  const t = useTranslatable()
  return (
    <section className="mb-6">
      <h3 className="mb-4 text-lg font-bold text-primaryTextColor">{t('faculty:according_to_semesters')}</h3>
      <div className="space-y-3 overflow-x-auto">
        {degrees.map((degree) => (
          <DegreeSemesterSection key={degree.key} degree={degree} />
        ))}
      </div>
    </section>
  )
}

function FormActionsSection(): JSX.Element | null {
  const t = useTranslatable()
  const canManageFaculties = useAdminAccess().canManage('faculties')
  if (!canManageFaculties) return null
  return (
    <div className="mt-8 border-t border-borderGrey pt-6">
      <div className="flex justify-end gap-3">
        <Button type="submit" variant="blue" className="min-w-[120px]">
          {t('common:save')}
        </Button>
      </div>
    </div>
  )
}

function DegreeSemesterSection({ degree }: { readonly degree: DegreeItem }): JSX.Element {
  const formik = useFormikContext<FormValues>()
  const [isExpanded, setIsExpanded] = useState(true)
  const t = useTranslatable()
  const semesterKey = degree.key
  const selectedCount = ((formik.values[semesterKey] as number[] | undefined) ?? []).length

  return (
    <div className="overflow-hidden rounded-card border border-borderGrey bg-card">
      <button
        type="button"
        className="flex w-full cursor-pointer items-center justify-between gap-4 bg-gradient-to-r from-card to-card/80 p-4 text-left transition-colors hover:from-card/90"
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <div className="flex items-center gap-3">
          <div
            className={classNames(
              'flex size-8 items-center justify-center rounded-full bg-primaryBlue/10 transition-transform duration-300',
              isExpanded ? 'rotate-0' : '-rotate-90'
            )}
          >
            <ArrowDown bold />
          </div>
          <div className="flex flex-col">
            <span className="font-semibold text-primaryTextColor">{degree.label}</span>
            <span className="text-xs text-captionColor">
              {t('common:selected')} {selectedCount}
            </span>
          </div>
        </div>
      </button>
      <div
        className={classNames(
          'grid transition-all duration-300 ease-in-out',
          isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="overflow-hidden">
          <SemesterGrid semesterKey={semesterKey} />
        </div>
      </div>
    </div>
  )
}

function SemesterGrid({ semesterKey }: { readonly semesterKey: string }): JSX.Element {
  const formik = useFormikContext<FormValues>()
  const t = useTranslatable()
  const formValues = formik.values
  const value = useMemo(() => (formValues[semesterKey] as number[] | undefined) ?? [], [formValues, semesterKey])

  const selectAll = useCallback(() => {
    formik.setFieldValue(semesterKey, SEMESTERS)
  }, [formik, semesterKey])

  const deselectAll = useCallback(() => {
    formik.setFieldValue(semesterKey, [])
  }, [formik, semesterKey])

  const toggleSemester = useCallback(
    (semester: number) => {
      const current = (formik.values[semesterKey] as number[] | undefined) ?? []
      const next = current.includes(semester)
        ? current.filter((s) => s !== semester)
        : [...current, semester].sort((a, b) => a - b)
      formik.setFieldValue(semesterKey, next)
    },
    [formik, semesterKey]
  )

  return (
    <div className="flex flex-col gap-3 border-t border-borderGrey p-4">
      <div className="flex justify-end gap-2">
        <button type="button" onClick={selectAll} className="text-xs text-primaryBlueLink hover:underline">
          {t('common:select_all')}
        </button>
        <span className="text-captionColor">|</span>
        <button type="button" onClick={deselectAll} className="text-xs text-primaryBlueLink hover:underline">
          {t('common:deselect_all')}
        </button>
      </div>
      <div className="rounded-md border border-borderGrey bg-card p-3">
        <div className="sm:grid-cols-15 grid grid-cols-10 gap-2">
          {SEMESTERS.map((semester) => (
            <SemesterChip
              key={semester}
              semester={semester}
              checked={value.includes(semester)}
              onToggle={() => toggleSemester(semester)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

const SemesterChip = React.memo(function SemesterChip({
  semester,
  checked,
  onToggle,
}: {
  readonly semester: number
  readonly checked: boolean
  readonly onToggle: () => void
}): JSX.Element {
  const id = `semester-${semester}-${React.useId()}`
  return (
    <label
      htmlFor={id}
      className={classNames(
        'relative inline-flex cursor-pointer items-center justify-center rounded border p-1.5 transition-colors',
        checked
          ? 'border-primaryBlue bg-primaryBlue/10 text-primaryBlue'
          : 'border-borderGrey bg-card hover:border-primaryBlue/50 hover:bg-gray-50 dark:hover:bg-gray-800'
      )}
    >
      <input type="checkbox" id={id} checked={checked} onChange={onToggle} className="peer sr-only" />
      <span className="text-xs font-medium text-primaryTextColor">{semester}</span>
    </label>
  )
})
